import { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useTranslation } from "react-i18next";
import * as IBAN from "iban";
import type {
  CommoditySignTokenData,
  CommoditySignBillingInfo,
  CommoditySignPayload,
} from "../../api/commoditySigning";
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Session-free signing modal for the public /sign-commodity/:token page:
// everything comes in through props (from the token GET), the sign POST is
// lifted to onSign — no Redux, no cookies, no authenticated endpoints.
interface CommoditySignTokenModalProps {
  data: CommoditySignTokenData;
  onSign: (payload: CommoditySignPayload) => Promise<void>;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatDate = (d: string | null) => {
  if (!d) return null;
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString("en-GB");
};

const formatFee = (v: number | null, suffix: string) => (v == null ? "—" : `€ ${v} ${suffix}`);

export const CommoditySignTokenModal = ({ data, onSign }: CommoditySignTokenModalProps) => {
  const { t } = useTranslation("addservice");
  const [signatureName, setSignatureName] = useState("");
  const [signeeFunction, setSigneeFunction] = useState("");
  const [signatureMode, setSignatureMode] = useState<"type" | "draw" | "upload">("type");
  const [canvasHasDrawing, setCanvasHasDrawing] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [hasViewedAllPages, setHasViewedAllPages] = useState(false);
  const [documentPages, setDocumentPages] = useState({ offtake: 0, injection: 0 });
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [ibanValid, setIbanValid] = useState<boolean | null>(data.billing_info.iban ? IBAN.isValid(data.billing_info.iban.replace(/\s/g, "").toUpperCase()) : null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const viewedPagesRef = useRef<Set<number>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  // The pointer that owns the in-progress stroke, so concurrent pointers (a second finger,
  // palm) can be ignored instead of hijacking or prematurely ending the signature.
  const activePointerIdRef = useRef<number | null>(null);

  const [billingInfo, setBillingInfo] = useState<CommoditySignBillingInfo>({ ...data.billing_info });
  const [companyEmail, setCompanyEmail] = useState(data.company_email);

  const pdfUrls = data.pdf_urls;
  const hasValidPdfs = !!(pdfUrls.offtake || pdfUrls.injection);
  const { contract } = data;
  const startDate = formatDate(contract.start_date);
  const effectiveStartDate = formatDate(contract.effective_start_date);
  const startDateRolled =
    contract.start_date && contract.effective_start_date && contract.start_date !== contract.effective_start_date;

  useEffect(() => {
    if (!hasValidPdfs) setPdfLoadError(t("signingPage.commodityNoPdf"));
  }, [hasValidPdfs, t]);

  const validateIBAN = (value: string) => {
    const cleanIBAN = value.replace(/\s/g, "").toUpperCase();
    if (!cleanIBAN) {
      setIbanValid(null);
      return;
    }
    setIbanValid(IBAN.isValid(cleanIBAN));
  };

  const formatIBAN = (value: string) => {
    const cleanValue = value.replace(/\s/g, "").toUpperCase();
    return cleanValue.match(/.{1,4}/g)?.join(" ") || cleanValue;
  };

  const handleBillingChange = (field: keyof CommoditySignBillingInfo, value: string) => {
    if (field === "iban") {
      value = formatIBAN(value);
      validateIBAN(value);
    }
    setBillingInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 10) setHasScrolledToEnd(true);
    if (totalPages > 0) {
      const pageHeight = scrollHeight / totalPages;
      const visiblePage = Math.ceil((scrollTop + clientHeight / 2) / pageHeight);
      setCurrentPage(Math.min(visiblePage, totalPages));
      viewedPagesRef.current.add(visiblePage);
      if (viewedPagesRef.current.size === totalPages) setHasViewedAllPages(true);
    }
  };

  const handleAcceptTermsChange = (checked: boolean) => {
    setHasAcceptedTerms(checked);
    if (checked && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: "smooth" });
      setTimeout(() => {
        setHasScrolledToEnd(true);
        setHasViewedAllPages(true);
        for (let i = 1; i <= totalPages; i++) viewedPagesRef.current.add(i);
      }, 500);
    }
  };

  const onDocumentLoadSuccess = (type: "offtake" | "injection", { numPages }: { numPages: number }) => {
    setDocumentPages((prev) => ({ ...prev, [type]: numPages }));
    setTotalPages((prev) => prev + numPages);
    if ((type === "offtake" && !pdfUrls.injection) || (type === "injection" && !pdfUrls.offtake)) {
      viewedPagesRef.current.add(1);
    }
    setTimeout(() => {
      const container = scrollContainerRef.current;
      if (container && container.scrollHeight <= container.clientHeight) {
        setHasScrolledToEnd(true);
        setHasViewedAllPages(true);
      }
    }, 100);
    setPdfLoadError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("Error loading PDF:", error);
    setPdfLoadError(t("signingPage.commodityPdfLoadError"));
  };

  // Pointer Events unify mouse, touch and pen so a customer can finger-sign on a
  // touchscreen. setPointerCapture keeps the stroke flowing when the pointer leaves the
  // canvas bounds, which replaces the old document-level mousemove/mouseup listeners.
  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Only the PRIMARY contact (left mouse / first finger, button === 0) starts a stroke,
    // and only when none is already active — a second finger or a right/middle button must
    // not hijack the in-progress signature.
    if (isDrawingRef.current || e.button !== 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    isDrawingRef.current = true;
    activePointerIdRef.current = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height),
    );
  };

  const handleMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Only the pointer that started the stroke draws, and only while its primary
    // button/contact is held (buttons === 1 for a finger down or left-mouse held) — a
    // right/middle-button drag, or a stray move after release, must not add ink.
    if (!isDrawingRef.current || e.pointerId !== activePointerIdRef.current || e.buttons !== 1) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height),
    );
    ctx.strokeStyle = "#04344d";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    setCanvasHasDrawing(true);
  };

  const stopDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Only the active pointer ends the stroke; a second finger lifting must leave it running.
    if (!isDrawingRef.current || e.pointerId !== activePointerIdRef.current) return;
    isDrawingRef.current = false;
    activePointerIdRef.current = null;
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
  };

  useEffect(() => {
    // A mode switch remounts a fresh blank <canvas>; the drawn bitmap cannot survive
    // unmount, so a stale canvasHasDrawing would pass validation on an empty canvas
    // and submit a blank signature. Reset the flag on every mode change.
    setCanvasHasDrawing(false);
  }, [signatureMode]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    setCanvasHasDrawing(false);
  };

  const buildSignatureImage = (): string | null => {
    if (signatureMode === "draw") {
      return canvasRef.current?.toDataURL("image/png") ?? null;
    }
    if (signatureMode === "upload") {
      return uploadedSignature;
    }
    const offscreen = document.createElement("canvas");
    offscreen.width = 300;
    offscreen.height = 80;
    const ctx = offscreen.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 300, 80);
    ctx.font = "italic 28px Georgia, serif";
    ctx.fillStyle = "#04344d";
    ctx.textBaseline = "middle";
    ctx.fillText(signatureName.trim(), 16, 40);
    return offscreen.toDataURL("image/png");
  };

  const handleSign = async () => {
    if (!signatureName.trim()) {
      setFormError(t("signingPage.commodityNameRequired"));
      return;
    }
    if (!signeeFunction.trim()) {
      setFormError(t("signingPage.commodityFunctionRequired"));
      return;
    }
    const signatureValid =
      signatureMode === "type"
        ? signatureName.trim().length > 0
        : signatureMode === "draw"
          ? canvasHasDrawing
          : uploadedSignature !== null;
    if (!signatureValid) {
      setFormError(t("signingPage.commoditySignatureRequired"));
      return;
    }
    if (!hasAcceptedTerms) {
      setFormError(t("signingPage.commodityTermsRequired"));
      return;
    }
    if (!hasScrolledToEnd && !hasViewedAllPages) {
      setFormError(t("signingPage.commodityReviewRequired"));
      return;
    }
    if (ibanValid === false) {
      setFormError(t("signingPage.commodityIbanInvalid"));
      return;
    }
    if (!isValidEmail(companyEmail.trim())) {
      setFormError(t("signingPage.commodityEmailInvalid"));
      return;
    }
    setFormError(null);
    setIsProcessing(true);
    try {
      await onSign({
        name: signatureName.trim(),
        ...(signeeFunction.trim() ? { signee_function: signeeFunction.trim() } : {}),
        signature_image: buildSignatureImage(),
        billingInfo,
        companyEmail: companyEmail.trim(),
      });
    } catch (err) {
      // The page maps terminal states (410/409); anything re-thrown here just
      // re-enables the button. Show the message if the page attached one.
      if (err instanceof Error && err.message) setFormError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const directionLabel =
    contract.direction === "combined"
      ? t("signingPage.commodityCombined")
      : contract.direction === "offtake"
        ? t("signingPage.commodityOfftake")
        : t("signingPage.commodityInjection");

  const inputClass =
    "w-full border border-gray-200 rounded-lg p-2.5 text-sm text-primary-600 bg-white placeholder:text-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-300";
  const labelClass = "block text-xs font-medium text-primary-500 mb-1";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] flex">
        {/* Left panel — PDF viewer */}
        <div className="flex-1 flex flex-col border-r border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {directionLabel} — {contract.company_name}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {totalPages > 0 && t("signingPage.commodityPageOf", { current: currentPage, total: totalPages })}
                  {hasViewedAllPages && (
                    <span className="ml-2 text-primary-600 font-medium">✓ {t("signingPage.commodityReviewed")}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {pdfUrls.offtake && (
                  <a
                    href={pdfUrls.offtake}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    ⬇ {t("signingPage.commodityOfftake")}
                  </a>
                )}
                {pdfUrls.injection && (
                  <a
                    href={pdfUrls.injection}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    ⬇ {t("signingPage.commodityInjection")}
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-hidden bg-gray-50 p-4">
            {hasValidPdfs ? (
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="h-full overflow-y-auto bg-white rounded-lg shadow-inner p-4"
              >
                {pdfUrls.offtake && (
                  <>
                    <div className="mb-4 py-3 px-4 bg-primary-50 border-l-4 border-primary-600 rounded">
                      <h3 className="text-lg font-semibold text-primary-700">
                        {t("signingPage.commodityOfftakeDocument")}
                      </h3>
                    </div>
                    <Document
                      file={pdfUrls.offtake}
                      onLoadSuccess={(info) => onDocumentLoadSuccess("offtake", info)}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex justify-center items-center h-96">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                        </div>
                      }
                      className="flex flex-col items-center"
                    >
                      {documentPages.offtake > 0 &&
                        Array.from(new Array(documentPages.offtake), (_, index) => (
                          <div key={`offtake_page_${index + 1}`} className="mb-4">
                            <Page
                              pageNumber={index + 1}
                              width={Math.min(700, window.innerWidth - 450)}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              className="shadow-lg"
                            />
                          </div>
                        ))}
                    </Document>
                  </>
                )}

                {pdfUrls.injection && (
                  <>
                    {pdfUrls.offtake && <div className="my-8 border-b-2 border-gray-300 w-full"></div>}
                    <div className="mb-4 py-3 px-4 bg-secondary-50 border-l-4 border-secondary-600 rounded">
                      <h3 className="text-lg font-semibold text-secondary-700">
                        {t("signingPage.commodityInjectionDocument")}
                      </h3>
                    </div>
                    <Document
                      file={pdfUrls.injection}
                      onLoadSuccess={(info) => onDocumentLoadSuccess("injection", info)}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex justify-center items-center h-96">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                        </div>
                      }
                      className="flex flex-col items-center"
                    >
                      {documentPages.injection > 0 &&
                        Array.from(new Array(documentPages.injection), (_, index) => (
                          <div key={`injection_page_${index + 1}`} className="mb-4">
                            <Page
                              pageNumber={index + 1}
                              width={Math.min(700, window.innerWidth - 450)}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              className="shadow-lg"
                            />
                          </div>
                        ))}
                    </Document>
                  </>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">{pdfLoadError || t("signingPage.commodityNoPdf")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right panel — summary, billing, signature */}
        <div className="w-[420px] flex flex-col bg-gray-50 overflow-y-auto">
          <div className="p-5 border-b border-gray-200 bg-white">
            <h3 className="text-sm font-semibold text-primary-700 mb-3">{t("signingPage.commoditySummary")}</h3>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-primary-400">EAN</dt>
                <dd className="font-medium text-primary-700">{contract.ean}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-primary-400">{t("signingPage.commodityStartDate")}</dt>
                <dd className="font-medium text-primary-700">{effectiveStartDate ?? startDate ?? "—"}</dd>
              </div>
              {startDateRolled && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                  {t("signingPage.commodityStartDateRolled", { original: startDate, effective: effectiveStartDate })}
                </p>
              )}
              <div className="flex justify-between">
                <dt className="text-primary-400">{t("signingPage.commodityEndDate")}</dt>
                <dd className="font-medium text-primary-700">
                  {formatDate(contract.end_date) ?? t("signingPage.commodityFlexible")}
                </dd>
              </div>
              {contract.product && (
                <div className="flex justify-between">
                  <dt className="text-primary-400">{t("signingPage.commodityProduct")}</dt>
                  <dd className="font-medium text-primary-700">{contract.product}</dd>
                </div>
              )}
              {contract.fees.injection && (
                <div className="flex justify-between">
                  <dt className="text-primary-400">{t("signingPage.commodityInjectionFee")}</dt>
                  <dd className="font-medium text-primary-700">
                    {formatFee(contract.fees.injection.platform_fee, "/ MWh")}
                  </dd>
                </div>
              )}
              {contract.fees.offtake && (
                <div className="flex justify-between">
                  <dt className="text-primary-400">{t("signingPage.commodityOfftakeFee")}</dt>
                  <dd className="font-medium text-primary-700">
                    {formatFee(contract.fees.offtake.platform_fee, "/ MWh")}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div className="p-5 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-primary-700 mb-3">{t("signingPage.commodityBilling")}</h3>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>{t("signingPage.commodityBillingEmail")}</label>
                <input
                  type="email"
                  className={inputClass}
                  value={billingInfo.billingEmail}
                  onChange={(e) => handleBillingChange("billingEmail", e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>IBAN</label>
                <input
                  type="text"
                  className={`${inputClass} ${ibanValid === false ? "border-red-400" : ""}`}
                  value={billingInfo.iban}
                  onChange={(e) => handleBillingChange("iban", e.target.value)}
                  placeholder="BE00 0000 0000 0000"
                />
                {ibanValid === false && (
                  <p className="text-xs text-red-600 mt-1">{t("signingPage.commodityIbanInvalid")}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className={labelClass}>{t("signingPage.commodityStreet")}</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={billingInfo.billingAddressStreet}
                    onChange={(e) => handleBillingChange("billingAddressStreet", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t("signingPage.commodityNumber")}</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={billingInfo.billingAddressNumber}
                    onChange={(e) => handleBillingChange("billingAddressNumber", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={labelClass}>{t("signingPage.commodityZip")}</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={billingInfo.billingAddressZip}
                    onChange={(e) => handleBillingChange("billingAddressZip", e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>{t("signingPage.commodityCity")}</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={billingInfo.billingAddressCity}
                    onChange={(e) => handleBillingChange("billingAddressCity", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>{t("signingPage.commodityCompanyEmail")}</label>
                <input
                  type="email"
                  className={inputClass}
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="p-5 mt-auto bg-white border-t border-gray-200">
            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={hasAcceptedTerms}
                onChange={(e) => handleAcceptTermsChange(e.target.checked)}
              />
              <span className="text-xs text-primary-600">{t("signingPage.commodityTerms")}</span>
            </label>
            <div className="mb-4">
              <label className={labelClass}>{t("signingPage.commoditySignatureHeading")}</label>
              <div className="flex border border-gray-200 rounded-lg overflow-hidden mb-2">
                <button
                  onClick={() => setSignatureMode("type")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                    signatureMode === "type" ? "bg-primary-700 text-white" : "bg-white text-primary-500 hover:bg-gray-50"
                  }`}
                >
                  {t("signingPage.commoditySignatureModeType")}
                </button>
                <button
                  onClick={() => setSignatureMode("draw")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                    signatureMode === "draw" ? "bg-primary-700 text-white" : "bg-white text-primary-500 hover:bg-gray-50"
                  }`}
                >
                  {t("signingPage.commoditySignatureModeDraw")}
                </button>
                <button
                  onClick={() => setSignatureMode("upload")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                    signatureMode === "upload"
                      ? "bg-primary-700 text-white"
                      : "bg-white text-primary-500 hover:bg-gray-50"
                  }`}
                >
                  {t("signingPage.commoditySignatureModeUpload")}
                </button>
              </div>

              {signatureMode === "type" ? (
                <div className="h-12 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                  <span
                    className="text-primary-900 opacity-80"
                    style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "18px" }}
                  >
                    {signatureName || "—"}
                  </span>
                </div>
              ) : signatureMode === "draw" ? (
                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    width={258}
                    height={120}
                    onPointerDown={startDrawing}
                    onPointerMove={handleMove}
                    onPointerUp={stopDrawing}
                    onPointerCancel={stopDrawing}
                    className="border border-gray-200 rounded-lg bg-white cursor-crosshair w-full"
                    style={{ touchAction: "none" }}
                  />
                  {!canvasHasDrawing && (
                    <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-300 pointer-events-none">
                      {t("signingPage.commodityDrawHere")}
                    </span>
                  )}
                  {canvasHasDrawing && (
                    <button
                      onClick={clearCanvas}
                      className="absolute bottom-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600"
                    >
                      {t("signingPage.commodityClearSignature")}
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  {uploadedSignature ? (
                    <div className="relative border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                      <img
                        src={uploadedSignature}
                        alt="Uploaded signature"
                        className="w-full h-[120px] object-contain p-2"
                      />
                      <button
                        onClick={() => setUploadedSignature(null)}
                        className="absolute top-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600 bg-white rounded px-1.5 py-0.5 border border-gray-200"
                      >
                        {t("signingPage.commodityUploadRemove")}
                      </button>
                    </div>
                  ) : (
                    <label
                      className="flex flex-col items-center justify-center h-[120px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 hover:border-gray-400 transition-colors"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
                          const reader = new FileReader();
                          reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                    >
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6 text-gray-400 mb-1.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                        />
                      </svg>
                      <span className="text-xs text-gray-500 text-center px-2">
                        {t("signingPage.commodityUploadDropHint")}
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className={labelClass}>{t("signingPage.commoditySignName")}</label>
              <input
                type="text"
                className={inputClass}
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder={t("signingPage.commoditySignNamePlaceholder")}
              />
            </div>
            <div className="mb-4">
              <label className={labelClass}>{t("signingPage.commoditySignFunction")}</label>
              <input
                type="text"
                className={inputClass}
                value={signeeFunction}
                onChange={(e) => setSigneeFunction(e.target.value)}
                placeholder={t("signingPage.commoditySignFunctionPlaceholder")}
              />
            </div>
            {formError && <p className="text-xs text-red-600 mb-3">{formError}</p>}
            <button
              onClick={handleSign}
              disabled={isProcessing}
              className="w-full py-3 px-6 text-sm bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? t("signingPage.commoditySigning") : t("signingPage.commoditySignButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
